import { Component, OnDestroy, ChangeDetectionStrategy, computed, afterRenderEffect, ElementRef, inject, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HioUserinfoService } from './hio-userinfo.service';
import { HioUserinfoEntryComponent } from './hio-userinfo-entry/hio-userinfo-entry.component';

/** Über .cdk-overlay-container (Office setzt 9999), damit Dialoge nicht durchklickbar bleiben. */
const BLOCKSCREEN_Z = '10000';
const CENTERINFO_Z = '10001';

@Component({
    selector: 'lib-hio-userinfo',
    template: `
@if (showBlockscreen()) {
  <div #blockscreen class="blockscreen" (click)="$event.stopPropagation()" (dblclick)="$event.stopPropagation()">
  </div>
}
<!-- Side -->
<div class="sideinfo">
  @for (info of infos(); track info) {
    @if (info.position == 'side') {
      <hio-userinfo-entry [info]="info"></hio-userinfo-entry>
    }
  }
</div>
<!-- Center -->
<div #centerinfo class="centerinfo">
  <div style="flex: 1;"></div>
  <div style="min-height: 10;">
    @for (info of infos(); track info) {
      @if (info.position == 'center') {
        <hio-userinfo-entry [info]="info"></hio-userinfo-entry>
      }
    }
  </div>
  <div style="flex: 1;"></div>
</div>
`,
    styles: [`
    :host {
      z-index: 999999999;
      display: block;
      position: absolute;
      left: 0; top: 0; right: 0; bottom: 0;
      pointer-events: none;
    }

    .centerinfo {
      margin: auto;
      width: 400px;
      max-width: 100%;
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .centerinfo.on-body {
      position: fixed;
      left: 0; top: 0; right: 0; bottom: 0;
      margin: 0;
      width: auto;
      max-width: none;
      z-index: ${CENTERINFO_Z};
      pointer-events: none;
    }

    .sideinfo {
      position: absolute;
      right: 3px;
      bottom: 3px;
      width: 400px;
      max-width: 100%;
    }

    .blockscreen {
      display: block;
      position: fixed;
      left: 0; top: 0; right: 0; bottom: 0;
      pointer-events: all;
      background-color: rgba(0,0,0,.4);
      z-index: ${BLOCKSCREEN_Z};
      cursor: wait;
    }
  `],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [HioUserinfoEntryComponent]
})
export class HioUserinfoComponent implements OnDestroy {
  private host = inject(ElementRef<HTMLElement>);
  private blockscreen = viewChild<ElementRef<HTMLElement>>('blockscreen');
  private centerinfo = viewChild<ElementRef<HTMLElement>>('centerinfo');

  /** Infos als Signal, damit zoneless CD bei BehaviorSubject.next greift. */
  public infos = toSignal(this.userInfo.infos, { initialValue: [] });
  /** Overlay: Server-Blockscreen-Signal ODER manuell blockierende Infos. */
  public showBlockscreen = computed(() =>
    this.userInfo.isBlocked() || this.infos().some(info => info.blockscreen)
  );

  constructor(public userInfo: HioUserinfoService) {
    afterRenderEffect(() => {
      this.showBlockscreen();
      this.infos();
      this.syncOverlayLayer();
    });
  }

  ngOnDestroy(): void {
    this.blockscreen()?.nativeElement.remove();
    const center = this.centerinfo()?.nativeElement;
    if (center?.parentElement === document.body)
      this.host.nativeElement.appendChild(center);
  }

  /**
   * Blockscreen und Center-Loading liegen in der App hinter .cdk-overlay-container.
   * Nur diese beiden Knoten auf body legen — nicht den ganzen Host, sonst liegen
   * Dialog-Selects (ebenfalls Overlay) hinter einer unsichtbaren Vollfläche.
   */
  private syncOverlayLayer() {
    const show = this.showBlockscreen();
    const block = this.blockscreen()?.nativeElement;
    const center = this.centerinfo()?.nativeElement;

    if (show) {
      if (block && block.parentElement !== document.body)
        document.body.appendChild(block);
      if (center && center.parentElement !== document.body) {
        center.classList.add('on-body');
        document.body.appendChild(center);
      }
      return;
    }

    if (center?.parentElement === document.body) {
      center.classList.remove('on-body');
      this.host.nativeElement.appendChild(center);
    }
  }
}
